import React, { FC, useState } from "react";
import { Form, Modal, Button, Alert } from "react-bootstrap";
import { Formik } from "formik";
import * as yup from "yup";
import { UserModel } from "../../../models/UserModel";
import { UserType } from "../../../types/UserType";
import ClipLoader from "react-spinners/ClipLoader";
import { useAuth } from "../../../hooks/useAuth";
import { AuthContext } from "../../../providers/AuthContextProvider";
import { FormattedMessage, useIntl } from "react-intl";
import { LANG_COMMON_CANCEL, LANG_COMMON_CLINIC, LANG_COMMON_NAME, LANG_COMMON_NEW, LANG_COMMON_PASSWORD, LANG_COMMON_PASSWORD_MSG, LANG_COMMON_PAYMENT, LANG_COMMON_PROFILE, LANG_COMMON_SAVE, LANG_COMMON_USER, LANG_COMMON_USERS, LANG_ERROR, LANG_LIMIT_ERROR, LANG_WARNING_FILL_ALL } from "../../../lang";
import BusinessSelect from "../../../components/form/BusinessSelect";
import { useAPI } from "../../../hooks/useAPI";

interface Props {
    show: boolean
    onHide: () => void
    onSave: () => void
    plan?: {
        id: string;
        name: string;
        price: number;
        description: string;
        recurrency: string;
        max_persons: number;
        max_users: number;
    }
}

const PlanAddModal: FC<Props> = ({ show, onHide, onSave, plan }) => {
    const intl = useIntl();
    const { api } = useAPI();
    const schema = yup.object().shape({
        name: yup.string().required(),
        price: yup.number().required().positive(),
        description: yup.string().required(),
        recurrency: yup.string().required(),
        max_persons: yup.number().required().positive(),
        max_users: yup.number().required().positive(),
    });
    const [formWasSubmitted, setFormWasSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const initialValues = plan ? plan : {
        name: "",
        price: 0,
        description: "",
        recurrency: "M",
        max_persons: 0,
        max_users: 0,
    };

    const onSubmit = async (values: any) => {
        setLoading(true);
        setFormWasSubmitted(false);
        const req = !plan ?
            api().post(`/api/plan`, values)
            : api().put(`/api/plan/${plan.id}`, values);

        req.then((response) => {
            onSave();
        }).catch((error) => {
            setHasError(true);
            setErrorMsg(error.response.data.message);
        })
            .finally(() => {
                setLoading(false);
            });
    }

    return (
        <Modal
            show={show}
            onHide={() => {
                onHide();
                setFormWasSubmitted(false);
            }}
            className="modal-default"
        >
            <Formik
                validationSchema={schema}
                onSubmit={onSubmit}
                initialValues={initialValues}
            >
                {({
                    setFieldValue,
                    handleSubmit,
                    handleChange,
                    values,
                    touched,
                    isValid,
                    errors }) => (
                    <Form noValidate onSubmit={(e) => {
                        setFormWasSubmitted(true);
                        handleSubmit(e);
                    }}>
                        <Modal.Header closeButton closeVariant="white">
                            <Modal.Title>
                                Novo Plano
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {(!isValid && formWasSubmitted) && (<Alert variant={'danger'}>{intl.formatMessage(LANG_WARNING_FILL_ALL)}</Alert>)}
                            {hasError && (<Alert variant={'danger'}>{errorMsg}</Alert>)}
                            <Form.Group className="mb-3" controlId="name">
                                <Form.Label>{intl.formatMessage(LANG_COMMON_NAME)}</Form.Label>
                                <Form.Control
                                    type="text"
                                    onChange={handleChange}
                                    value={values.name}
                                    isInvalid={touched.name && !!errors.name}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.name}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="price">
                                <Form.Label>Preço</Form.Label>
                                <Form.Control
                                    type="number"
                                    onChange={handleChange}
                                    value={values.price}
                                    isInvalid={touched.price && !!errors.price}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.price}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="description">
                                <Form.Label>Descrição</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    onChange={handleChange}
                                    value={values.description}
                                    isInvalid={touched.description && !!errors.description}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.description}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="recurrency">
                                <Form.Label>Recorrência</Form.Label>
                                <Form.Select
                                    className="form-control"
                                    onChange={handleChange}
                                    value={values.recurrency}
                                    isInvalid={touched.recurrency && !!errors.recurrency}
                                >
                                    <option value="">Selecione</option>
                                    <option value="M">Mensal</option>
                                    <option value="Y">Anual</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.recurrency}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="max_persons">
                                <Form.Label>Máximo de Pessoas</Form.Label>
                                <Form.Control
                                    type="number"
                                    onChange={handleChange}
                                    value={values.max_persons}
                                    isInvalid={touched.max_persons && !!errors.max_persons}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.max_persons}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="max_users">
                                <Form.Label>Máximo de Usuários</Form.Label>
                                <Form.Control
                                    type="number"
                                    onChange={handleChange}
                                    value={values.max_users}
                                    isInvalid={touched.max_users && !!errors.max_users}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.max_users}
                                </Form.Control.Feedback>
                            </Form.Group>

                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="outline-light" onClick={onHide} className="btn-custom btn-custom-light">
                                {intl.formatMessage(LANG_COMMON_CANCEL)}
                            </Button>
                            <Button variant="primary" type={"submit"} disabled={loading} className="btn-custom bg-custom-gradient">
                                {loading ? <><ClipLoader color={'#ffffff'} loading={loading} size={'12px'} /> </> : intl.formatMessage(LANG_COMMON_SAVE)}
                            </Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    )
}

export default PlanAddModal;